import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { Plus, Pencil, Trash2, X, MapPin, Users, FileText, CheckSquare, QrCode, Building2, Check, Link2Off, Layers, Copy } from 'lucide-react';

function Modal({ title, onClose, wide, xl, children }) {
  return (
    <div className="modal-overlay">
      <div className={`modal ${wide ? 'modal-lg' : ''} ${xl ? 'modal-xl' : ''}`}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={15} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const BOSH = { ad: '', alan: '', aciklama: '', bina_id: '', kat_id: '', kurum_id: '', aktif: true };

export default function Lokasyonlar() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [binalar, setBinalar] = useState([]);
  const [katlar, setKatlar] = useState([]);
  const [kurumlar, setKurumlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'ekle' | lokasyon_obj
  const [form, setForm] = useState(BOSH);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedBina, setSelectedBina] = useState('');
  const [selectedKat, setSelectedKat] = useState('');
  const [filterKatlar, setFilterKatlar] = useState([]);
  const [atamaModal, setAtamaModal] = useState(null); // lokasyon obj

  const load = async () => {
    setLoading(true);
    try {
      // Her isteği ayrı yakala, birinin hatası diğerini etkilemesin
      const [lokRes, binRes] = await Promise.all([
        api.get('/lokasyonlar').catch(() => ({ data: [] })),
        api.get('/binalar').catch(() => ({ data: [] })),
      ]);
      setList(lokRes.data);
      setBinalar(binRes.data);

      if (user.rol === 'superadmin') {
        const kurRes = await api.get('/kurumlar').catch(() => ({ data: [] }));
        setKurumlar(kurRes.data);
      }
    } catch {
      toast.error('Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEkle = () => {
    setForm({ ...BOSH, kurum_id: user.rol === 'superadmin' ? '' : user.kurum_id });
    setKatlar([]);
    setModal('ekle');
  };

  const openDuzenle = async l => {
    // Önce katları yükle, sonra form'u kat_id ile set et
    let yuklenenKatlar = [];
    if (l.bina_id) {
      try { const { data } = await api.get(`/binalar/${l.bina_id}/katlar`); yuklenenKatlar = data; }
      catch { yuklenenKatlar = []; }
    }
    setKatlar(yuklenenKatlar);
    setForm({ ad: l.ad, alan: l.alan || '', aciklama: l.aciklama || '', bina_id: l.bina_id ? String(l.bina_id) : '', kat_id: l.kat_id ? String(l.kat_id) : '', kurum_id: l.kurum_id, aktif: l.aktif });
    setModal(l);
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'ekle') {
        await api.post('/lokasyonlar', { ...form, bina_id: form.bina_id || null, kat_id: form.kat_id || null });
        toast.success('Lokasyon eklendi');
      } else {
        await api.put(`/lokasyonlar/${modal.id}`, { ...form, bina_id: form.bina_id || null, kat_id: form.kat_id || null });
        toast.success('Lokasyon güncellendi');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    } finally {
      setSaving(false);
    }
  };

  const handleKopya = async l => {
    if (!window.confirm(`"${l.ad}" lokasyonunu kopyalamak istiyor musunuz?`)) return;
    try {
      await api.post('/lokasyonlar', {
        ad: l.ad + ' (Kopya)',
        bina_id: l.bina_id || null,
        kat_id: l.kat_id || null,
        alan: l.alan || '',
        aciklama: l.aciklama || '',
        kurum_id: l.kurum_id,
        aktif: l.aktif,
      });
      toast.success('Lokasyon kopyalandı');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Kopyalanamadı'); }
  };

    const handleDelete = async l => {
    if (!window.confirm(`"${l.ad}" lokasyonunu silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/lokasyonlar/${l.id}`);
      toast.success('Silindi');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Silinemedi');
    }
  };

  const binaFiltreli = binalar.filter(b =>
    !form.kurum_id || String(b.kurum_id) === String(form.kurum_id)
  );
  const handleBinaChange = async (binaId) => {
    setForm(f => ({ ...f, bina_id: binaId, kat_id: '' }));
    if (binaId) {
      try { const { data } = await api.get(`/binalar/${binaId}/katlar`); setKatlar(data); }
      catch { setKatlar([]); }
    } else { setKatlar([]); }
  };

  const filtered = list.filter(l => {
    const q = search.toLowerCase();
    const binaMatch = !selectedBina || String(l.bina_id) === selectedBina;
    const katMatch = !selectedKat || String(l.kat_id) === selectedKat;
    return binaMatch && katMatch && (`${l.ad} ${l.bina_adi || ''} ${l.kat_adi || ''}`).toLowerCase().includes(q);
  });

  // Bina + Kat gruplarına ayır
  const groups = {};
  filtered.forEach(l => {
    const binaKey = l.bina_adi || '—';
    const katKey = l.kat_adi || null;
    const key = binaKey;
    if (!groups[key]) groups[key] = { katGroups: {}, bina_id: l.bina_id };
    const kg = katKey || '__';
    if (!groups[key].katGroups[kg]) groups[key].katGroups[kg] = [];
    groups[key].katGroups[kg].push(l);
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Lokasyonlar</h1>
          <p className="page-subtitle">{list.length} lokasyon kayıtlı</p>
        </div>
        <button className="btn btn-primary" onClick={openEkle}>
          <Plus size={14} /> Yeni Lokasyon
        </button>
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <div className="search-box" style={{ flex: 1, minWidth: 200, maxWidth: 300 }}>
          <span className="search-icon">
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </span>
          <input className="input" placeholder="Lokasyon ara..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {binalar.length > 0 && (
          <select className="input" style={{ maxWidth: 200 }} value={selectedBina} onChange={async e => {
            setSelectedBina(e.target.value); setSelectedKat('');
            if (e.target.value) { try { const {data} = await api.get(`/binalar/${e.target.value}/katlar`); setFilterKatlar(data); } catch { setFilterKatlar([]); } }
            else setFilterKatlar([]);
          }}>
            <option value="">Tüm Binalar</option>
            {binalar.map(b => <option key={b.id} value={b.id}>{b.ad}</option>)}
          </select>
        )}
        {filterKatlar.length > 0 && (
          <select className="input" style={{ maxWidth: 200 }} value={selectedKat} onChange={e => setSelectedKat(e.target.value)}>
            <option value="">Tüm Katlar</option>
            {filterKatlar.map(k => <option key={k.id} value={String(k.id)}>{k.ad}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-blue" style={{ width: 28, height: 28 }} />
        </div>
      ) : !filtered.length ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
          {search ? 'Arama sonucu bulunamadı' : 'Henüz lokasyon eklenmemiş. Yeni lokasyon ekleyin.'}
        </div>
      ) : (
        Object.entries(groups).map(([bina, grpData]) => {
          const allLoks = Object.values(grpData.katGroups).flat();
          return (
          <div key={bina} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Building2 size={14} color="var(--blue)" />
              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{bina}</span>
              <span className="badge badge-gray">{allLoks.length}</span>
            </div>
            {Object.entries(grpData.katGroups).map(([katKey, loks]) => (
              <div key={katKey} style={{ marginBottom: katKey !== '__' ? 12 : 0 }}>
                {katKey !== '__' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, marginLeft: 4 }}>
                    <Layers size={12} color="var(--blue)" />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)' }}>{katKey}</span>
                    <span className="badge badge-gray" style={{ fontSize: 10 }}>{loks.length}</span>
                  </div>
                )}
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Lokasyon</th>
                      <th>Alan / Kat</th>
                      <th>Anket</th>
                      <th>Checklist</th>
                      <th>Personel</th>
                      <th>QR</th>
                      <th>Durum</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loks.map(l => (
                      <tr key={l.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,rgba(74,108,247,0.12),rgba(74,108,247,0.06))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <MapPin size={13} color="var(--blue)" />
                            </div>
                            <span style={{ fontWeight: 600 }}>{l.ad}</span>
                          </div>
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          {l.kat_adi && <span style={{display:'flex',alignItems:'center',gap:4,marginBottom:2,color:'var(--blue)',fontWeight:500}}><Layers size={11}/>{l.kat_adi}</span>}
                          {l.alan || ''}
                          {!l.kat_adi && !l.alan && '—'}
                        </td>
                        <td>
                          {l.anket_baslik
                            ? <span className="badge badge-blue" title={l.anket_baslik}>{l.anket_baslik.length > 22 ? l.anket_baslik.slice(0, 22) + '…' : l.anket_baslik}</span>
                            : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Yok</span>
                          }
                        </td>
                        <td>
                          {l.checklist_baslik
                            ? <span className="badge badge-green" title={l.checklist_baslik}>{l.checklist_baslik.length > 22 ? l.checklist_baslik.slice(0, 22) + '…' : l.checklist_baslik}</span>
                            : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Yok</span>
                          }
                        </td>
                        <td>
                          {l.personel_sayisi > 0
                            ? <span className="badge badge-gray">{l.personel_sayisi} kişi</span>
                            : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                        <td>
                          {l.qr_uuid
                            ? <span className="badge badge-green"><QrCode size={10} /> Var</span>
                            : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                        <td>
                          <span className={`badge ${l.aktif ? 'badge-green' : 'badge-red'}`}>{l.aktif ? 'Aktif' : 'Pasif'}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setAtamaModal(l)}
                              title="Anket / Checklist / Personel Atama"
                            >
                              <Link2Off size={12} /> Atama
                            </button>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleKopya(l)} title="Kopyala"><Copy size={12} /></button>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openDuzenle(l)}><Pencil size={12} /></button>
                            <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(l)}><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
              </div>
            ))}
          </div>
          );
        })
      )}

      {/* Ekle / Düzenle Modal */}
      {modal && (
        <Modal title={modal === 'ekle' ? 'Yeni Lokasyon' : `Düzenle: ${modal.ad}`} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {user.rol === 'superadmin' && (
                <div className="input-group">
                  <label className="input-label">Kurum *</label>
                  <select className="input" required value={form.kurum_id} onChange={e => setForm({ ...form, kurum_id: e.target.value, bina_id: '' })}>
                    <option value="">Seçin...</option>
                    {kurumlar.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
              )}
              <div className="input-group">
                <label className="input-label">Bina</label>
                <select className="input" value={form.bina_id} onChange={e => handleBinaChange(e.target.value)}>
                  <option value="">Binasız</option>
                  {binaFiltreli.map(b => <option key={b.id} value={b.id}>{b.ad}</option>)}
                </select>
              </div>
              <div className="form-grid">
                <div className="input-group full">
                  <label className="input-label">Lokasyon Adı *</label>
                  <input className="input" required value={form.ad} onChange={e => setForm({ ...form, ad: e.target.value })} placeholder="WC, Lobi, Toplantı Odası..." />
                </div>
                {katlar.length > 0 && (
                  <div className="input-group">
                    <label className="input-label"><Layers size={12} style={{verticalAlign:'middle',marginRight:4}} />Kat / Servis</label>
                    <select className="input" value={form.kat_id} onChange={e => setForm({ ...form, kat_id: e.target.value })}>
                      <option value="">Kat seçin (opsiyonel)</option>
                      {katlar.map(k => <option key={k.id} value={String(k.id)}>{k.ad}</option>)}
                    </select>
                  </div>
                )}
                <div className="input-group">
                  <label className="input-label">Alan / Not</label>
                  <input className="input" value={form.alan} onChange={e => setForm({ ...form, alan: e.target.value })} placeholder="Ek not, alan bilgisi..." />
                </div>
                <div className="input-group">
                  <label className="input-label">Durum</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
                    <input type="checkbox" checked={form.aktif} onChange={e => setForm({ ...form, aktif: e.target.checked })} />
                    <span style={{ fontSize: 13 }}>Aktif</span>
                  </label>
                </div>
                <div className="input-group full">
                  <label className="input-label">Açıklama</label>
                  <textarea className="input" rows={2} value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <div className="spinner" /> : null} Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Atama Modal */}
      {atamaModal && (
        <AtamaModal
          lokasyon={atamaModal}
          onClose={() => { setAtamaModal(null); load(); }}
          onUpdate={updated => setAtamaModal(prev => ({ ...prev, ...updated }))}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ATAMA MODAL
   - Bir lokasyona sadece bir anket ve bir checklist tanımlanabilir
   - Bir anket/checklist birden fazla lokasyona tanımlanabilir
══════════════════════════════════════════════════════════════ */
function AtamaModal({ lokasyon, onClose, onUpdate }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('anket');
  const [anketler, setAnketler] = useState([]);
  const [checklistler, setChecklistler] = useState([]);
  const [personeller, setPersoneller] = useState([]);

  // Seçili değerler (local state)
  const [seciliAnket, setSeciliAnket] = useState(lokasyon.anket_id ? String(lokasyon.anket_id) : '');
  const [seciliChecklist, setSeciliChecklist] = useState(lokasyon.checklist_id ? String(lokasyon.checklist_id) : '');
  const [seciliPersonel, setSeciliPersonel] = useState([]);

  const [savingAnket, setSavingAnket] = useState(false);
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [savingPersonel, setSavingPersonel] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      const [a, c, p, lp] = await Promise.all([
        api.get('/anketler').catch(() => ({ data: [] })),
        api.get('/checklistler').catch(() => ({ data: [] })),
        api.get('/kullanicilar/personeller/liste').catch(() => ({ data: [] })),
        api.get(`/lokasyonlar/${lokasyon.id}/personel`).catch(() => ({ data: [] })),
      ]);
      setAnketler(a.data);
      setChecklistler(c.data);
      setPersoneller(p.data);
      setSeciliPersonel(lp.data.map(p => p.id));
      setDataLoading(false);
    };
    loadData();
  }, [lokasyon.id]);

  const saveAnket = async () => {
    setSavingAnket(true);
    try {
      await api.put(`/lokasyonlar/${lokasyon.id}/anket`, { anket_id: seciliAnket || null });
      toast.success('Anket ataması güncellendi');
      const a = anketler.find(x => String(x.id) === seciliAnket);
      onUpdate({ anket_id: seciliAnket || null, anket_baslik: a?.baslik || null });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    } finally {
      setSavingAnket(false);
    }
  };

  const saveChecklist = async () => {
    setSavingChecklist(true);
    try {
      await api.put(`/lokasyonlar/${lokasyon.id}/checklist`, { checklist_id: seciliChecklist || null });
      toast.success('Checklist ataması güncellendi');
      const c = checklistler.find(x => String(x.id) === seciliChecklist);
      onUpdate({ checklist_id: seciliChecklist || null, checklist_baslik: c?.baslik || null });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    } finally {
      setSavingChecklist(false);
    }
  };

  const savePersonel = async () => {
    setSavingPersonel(true);
    try {
      await api.put(`/lokasyonlar/${lokasyon.id}/personel`, { personel_ids: seciliPersonel });
      toast.success('Personel ataması güncellendi');
      onUpdate({ personel_sayisi: seciliPersonel.length });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    } finally {
      setSavingPersonel(false);
    }
  };

  const togglePersonel = id => {
    setSeciliPersonel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title"><MapPin size={14} style={{ marginRight: 6, verticalAlign: 'middle', color: 'var(--blue)' }} />{lokasyon.ad}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {lokasyon.bina_adi ? `${lokasyon.bina_adi} · ` : ''}{lokasyon.alan || ''}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={15} /></button>
        </div>

        {/* Bilgi kutusu */}
        <div style={{ padding: '10px 20px', background: 'var(--gray-50)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--blue)' }}>ℹ</strong> Bir lokasyona en fazla 1 anket ve 1 checklist tanımlanabilir. Aynı şablon birden fazla lokasyona atanabilir.
        </div>

        {dataLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner spinner-blue" style={{ width: 24, height: 24 }} />
          </div>
        ) : (
          <div className="modal-body">
            <div className="tabs">
              <div className={`tab ${tab === 'anket' ? 'active' : ''}`} onClick={() => setTab('anket')}>
                <FileText size={13} /> Anket
              </div>
              <div className={`tab ${tab === 'checklist' ? 'active' : ''}`} onClick={() => setTab('checklist')}>
                <CheckSquare size={13} /> Checklist
              </div>
              <div className={`tab ${tab === 'personel' ? 'active' : ''}`} onClick={() => setTab('personel')}>
                <Users size={13} /> Personel
              </div>
            </div>

            {/* ─── ANKET TAB ─── */}
            {tab === 'anket' && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  QR kodu okutan müşteri bu anketi görecek. Sadece bir anket seçilebilir.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', marginBottom: 14 }}>
                  {/* Anket Yok seçeneği */}
                  <div
                    className={`select-card ${!seciliAnket ? 'selected' : ''}`}
                    onClick={() => setSeciliAnket('')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Anket yok (kaldır)</span>
                      {!seciliAnket && <Check size={15} color="var(--blue)" />}
                    </div>
                  </div>

                  {anketler.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: 13 }}>
                      Henüz anket şablonu oluşturulmamış.
                    </div>
                  )}

                  {anketler.map(a => (
                    <div
                      key={a.id}
                      className={`select-card ${String(a.id) === seciliAnket ? 'selected' : ''}`}
                      onClick={() => setSeciliAnket(String(a.id))}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{a.baslik}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {a.soru_sayisi} soru
                            {a.kurum_adi && ` · ${a.kurum_adi}`}
                          </div>
                        </div>
                        {String(a.id) === seciliAnket && <Check size={15} color="var(--blue)" />}
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary" onClick={saveAnket} disabled={savingAnket} style={{ width: '100%' }}>
                  {savingAnket ? <div className="spinner" /> : <Check size={14} />}
                  {seciliAnket ? `"${anketler.find(a => String(a.id) === seciliAnket)?.baslik || ''}" Anketini Ata` : 'Anketi Kaldır'}
                </button>
              </div>
            )}

            {/* ─── CHECKLİST TAB ─── */}
            {tab === 'checklist' && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Personel QR okuttuğunda bu checklist gösterilir. Sadece bir checklist seçilebilir.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', marginBottom: 14 }}>
                  <div
                    className={`select-card ${!seciliChecklist ? 'selected' : ''}`}
                    onClick={() => setSeciliChecklist('')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Checklist yok (kaldır)</span>
                      {!seciliChecklist && <Check size={15} color="var(--blue)" />}
                    </div>
                  </div>

                  {checklistler.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: 13 }}>
                      Henüz checklist şablonu oluşturulmamış.
                    </div>
                  )}

                  {checklistler.map(c => (
                    <div
                      key={c.id}
                      className={`select-card ${String(c.id) === seciliChecklist ? 'selected' : ''}`}
                      onClick={() => setSeciliChecklist(String(c.id))}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{c.baslik}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {c.soru_sayisi} madde
                            {c.kurum_adi && ` · ${c.kurum_adi}`}
                          </div>
                        </div>
                        {String(c.id) === seciliChecklist && <Check size={15} color="var(--blue)" />}
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary" onClick={saveChecklist} disabled={savingChecklist} style={{ width: '100%' }}>
                  {savingChecklist ? <div className="spinner" /> : <Check size={14} />}
                  {seciliChecklist ? `"${checklistler.find(c => String(c.id) === seciliChecklist)?.baslik || ''}" Listesini Ata` : 'Checklistı Kaldır'}
                </button>
              </div>
            )}

            {/* ─── PERSONEL TAB ─── */}
            {tab === 'personel' && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Bu lokasyona atanan personel checklist doldurabilir. Çoklu seçim yapılabilir.
                </p>
                {personeller.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: 13 }}>
                    Sistemde kayıtlı personel bulunamadı.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', marginBottom: 14 }}>
                      {personeller.map(p => {
                        const checked = seciliPersonel.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                              borderRadius: 10, border: `2px solid ${checked ? 'var(--blue)' : 'var(--border)'}`,
                              cursor: 'pointer', background: checked ? 'var(--gray-50)' : 'var(--bg-card)',
                              transition: 'all 0.12s',
                            }}
                          >
                            <input type="checkbox" checked={checked} onChange={() => togglePersonel(p.id)} style={{ flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{p.ad} {p.soyad}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.email}</div>
                            </div>
                            {checked && <Check size={14} color="var(--blue)" />}
                          </label>
                        );
                      })}
                    </div>
                    <button className="btn btn-primary" onClick={savePersonel} disabled={savingPersonel} style={{ width: '100%' }}>
                      {savingPersonel ? <div className="spinner" /> : <Users size={14} />}
                      {seciliPersonel.length} Personeli Kaydet
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}